import { Controller } from "@hotwired/stimulus"

// Handles the multi-step creation wizard + visual interactivity:
//   - Wizard navigation (next/prev step, panel show/hide, step indicator sync)
//   - Category pill selection (radio buttons)
//   - Platform pill toggle (checkboxes)
//   - File picker + preview
export default class extends Controller {
  static targets = ["requiredBadge", "customCategoryField", "panel", "stepDot", "urlField", "uploadModal", "submitBtn"]
  static values  = { step: Number }

  connect() {
    if (this.stepValue === 0) this.stepValue = 1
    this.#showCurrentPanel()
    this.#syncStepDots()
    this.#syncStatusPills()
    this.#syncUrlField()
    this.#syncCategoryPills()
    this.#syncPlatformPills()
    this.#syncCustomCategoryField()
    // Page « new » uniquement : ancre présente → corrige scroll restauré au refresh.
    requestAnimationFrame(() => {
      if (document.getElementById("app-project-wizard-scroll-top")) {
        this.#scrollAnchorIntoView(false)
      }
    })
  }

  // ── Wizard navigation ─────────────────────────────────────────────────────

  nextStep(event) {
    event.preventDefault()
    if (this.stepValue < this.panelTargets.length) {
      this.stepValue += 1
      this.#showCurrentPanel()
      this.#syncStepDots()
      this.#scrollAnchorIntoView(true)
    }
  }

  prevStep(event) {
    event.preventDefault()
    if (this.stepValue > 1) {
      this.stepValue -= 1
      this.#showCurrentPanel()
      this.#syncStepDots()
      this.#scrollAnchorIntoView(true)
    }
  }

  // ── Status segmented control ──────────────────────────────────────────────

  selectStatus(event) {
    const val = event.currentTarget.dataset.statusValue
    if (!val) return
    const radio = this.element.querySelector(`input[name="app_project[status]"][value="${val}"]`)
    if (radio) radio.checked = true
    this.#syncStatusPills()
  }

  syncStatusPills() {
    this.#syncStatusPills()
  }

  // ── Category pills ────────────────────────────────────────────────────────

  selectCategory(event) {
    const cat = event.currentTarget.dataset.category
    if (!cat) return
    const radio = this.element.querySelector(`input[value="${cat}"][type="radio"]`)
    if (radio) radio.checked = true
    this.#syncCategoryPills()
    this.#syncCustomCategoryField()
  }

  toggleCustomCategory() {
    this.#syncCustomCategoryField()
  }

  // ── Platform pills ────────────────────────────────────────────────────────

  updatePlatformPills() {
    this.#syncPlatformPills()
  }

  // ── File picker & preview ─────────────────────────────────────────────────

  openFilePicker(event) {
    event.preventDefault()
    const targetId = event.currentTarget.dataset.fileTarget
    const input = document.getElementById(targetId)
    if (!input) return
    if (typeof input.showPicker === "function") {
      input.showPicker()
    } else {
      input.click()
    }
  }

  handleFileSelection(event) {
    this.#showFileName(event)
    this.#previewFiles(event)
  }

  handleIconPreview(event) {
    const file = event.currentTarget.files?.[0]
    if (!file || !file.type.startsWith("image/")) return

    const btnId = event.currentTarget.dataset.previewBtnId
    const btn   = btnId ? document.getElementById(btnId) : null
    if (!btn) return

    const url = URL.createObjectURL(file)
    btn.innerHTML = ""
    btn.style.border  = "1.5px solid rgba(148,163,184,0.3)"
    btn.style.padding = "0"
    btn.style.overflow = "hidden"

    const img = document.createElement("img")
    img.src = url
    img.alt = file.name
    img.style.cssText = "width:100%;height:100%;object-fit:cover;border-radius:16px;"
    img.onload = () => URL.revokeObjectURL(url)
    btn.appendChild(img)
  }

  // ── Upload submit modal ───────────────────────────────────────────────────

  handleSubmit() {
    if (this.hasUploadModalTarget) {
      this.uploadModalTarget.style.display = "flex"
      document.body.style.overflow = "hidden"
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.style.opacity = "0.55"
      this.submitBtnTarget.style.cursor  = "not-allowed"
    }
  }

  // ── Focus states ──────────────────────────────────────────────────────────

  focusInput(event) {
    event.target.style.borderColor = "rgba(var(--ls-accent-rgb),0.5)"
    event.target.style.boxShadow   = "0 0 0 3px rgba(var(--ls-accent-rgb),0.08)"
  }

  blurInput(event) {
    event.target.style.borderColor = "rgba(15,23,42,0.12)"
    event.target.style.boxShadow   = "none"
  }

  // ── Private ───────────────────────────────────────────────────────────────

  #showCurrentPanel() {
    this.panelTargets.forEach((panel, i) => {
      const isActive = i + 1 === this.stepValue
      panel.classList.toggle("hidden", !isActive)
    })
  }

  #syncStepDots() {
    this.stepDotTargets.forEach((dot, i) => {
      dot.classList.toggle("step-primary", i + 1 <= this.stepValue)
    })
  }

  /** @param {boolean} smooth */
  #scrollAnchorIntoView(smooth) {
    const anchor = document.getElementById("app-project-wizard-scroll-top")
    const behavior = smooth ? "smooth" : "auto"
    if (anchor) {
      anchor.scrollIntoView({ behavior, block: "start" })
      return
    }
    window.scrollTo({ top: 0, behavior })
  }

  #syncStatusPills() {
    const radios = this.element.querySelectorAll('input[name="app_project[status]"]')
    radios.forEach(radio => {
      const pill = this.element.querySelector(`#status_pill_${radio.value}`)
      if (!pill) return
      if (radio.checked) {
        pill.style.background  = "var(--ls-accent)"
        pill.style.color       = "#fff"
        pill.style.boxShadow   = "0 1px 5px rgba(var(--ls-accent-rgb),0.3)"
      } else {
        pill.style.background  = "transparent"
        pill.style.color       = "rgba(15,23,42,0.5)"
        pill.style.boxShadow   = "none"
      }
    })
    this.#syncUrlField()
  }

  #syncUrlField() {
    if (!this.hasUrlFieldTarget) return
    const checked = this.element.querySelector('input[name="app_project[status]"]:checked')
    const isProduction = checked?.value === "production"
    this.urlFieldTarget.style.display = isProduction ? "block" : "none"
  }

  #syncCategoryPills() {
    const radios = this.element.querySelectorAll('input[name="app_project[category]"]')
    radios.forEach(radio => {
      const pill = this.element.querySelector(`#category_pill_${radio.value}`)
      if (!pill) return
      if (radio.checked) {
        pill.style.background  = "rgba(var(--ls-accent-rgb),0.08)"
        pill.style.borderColor = "rgba(var(--ls-accent-rgb),0.4)"
        pill.style.color       = "var(--ls-accent)"
      } else {
        pill.style.background  = "rgba(255,255,255,0.9)"
        pill.style.borderColor = "rgba(15,23,42,0.12)"
        pill.style.color       = "rgba(15,23,42,0.65)"
      }
    })
  }

  #syncPlatformPills() {
    const checkboxes = this.element.querySelectorAll('input[name="app_project[platform][]"]')
    checkboxes.forEach(cb => {
      const pill = this.element.querySelector(`#platform_pill_${cb.value}`)
      if (!pill) return
      if (cb.checked) {
        pill.style.background  = "rgba(var(--ls-accent-rgb),0.08)"
        pill.style.borderColor = "rgba(var(--ls-accent-rgb),0.4)"
        pill.style.color       = "var(--ls-accent)"
      } else {
        pill.style.background  = "rgba(255,255,255,0.9)"
        pill.style.borderColor = "rgba(15,23,42,0.12)"
        pill.style.color       = "rgba(15,23,42,0.65)"
      }
    })
  }

  #syncCustomCategoryField() {
    if (!this.hasCustomCategoryFieldTarget) return
    const radio = this.element.querySelector('input[name="app_project[category]"][value="other"]')
    const isOther = radio?.checked ?? false
    this.customCategoryFieldTarget.style.display = isOther ? "block" : "none"
    const input = this.customCategoryFieldTarget.querySelector("input")
    if (input) input.required = isOther
  }

  #showFileName(event) {
    const labelId = event.currentTarget.dataset.labelId
    const label   = document.getElementById(labelId)
    if (!label) return
    const files    = Array.from(event.currentTarget.files || [])
    const fallback = event.currentTarget.dataset.emptyLabel || ""
    label.textContent = files.map(f => f.name).join(", ") || fallback
  }

  #previewFiles(event) {
    const previewId  = event.currentTarget.dataset.previewId
    const preview    = document.getElementById(previewId)
    if (!preview) return
    const files = Array.from(event.currentTarget.files || []).filter(f => f.type.startsWith("image/"))
    preview.innerHTML = ""
    preview.style.display = files.length > 0 ? "flex" : "none"
    files.forEach(file => preview.appendChild(this.#buildPreviewItem(file, event.currentTarget.dataset.previewType)))
  }

  #buildPreviewItem(file, previewType) {
    const isIcon = previewType === "icon"
    const wrapper = document.createElement("div")
    wrapper.style.cssText = "display:flex;flex-direction:column;gap:6px;"

    const image = document.createElement("img")
    image.src   = URL.createObjectURL(file)
    image.alt   = file.name
    image.style.cssText = `width:${isIcon ? "72px" : "116px"};height:${isIcon ? "72px" : "80px"};object-fit:cover;border-radius:${isIcon ? "16px" : "10px"};border:1px solid rgba(15,23,42,0.12);`
    image.onload = () => URL.revokeObjectURL(image.src)

    const caption = document.createElement("span")
    caption.textContent  = file.name
    caption.style.cssText = `max-width:${isIcon ? "72px" : "116px"};font-size:11px;color:rgba(15,23,42,0.45);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`

    wrapper.appendChild(image)
    wrapper.appendChild(caption)
    return wrapper
  }
}
